// Undo/redo genérico para qualquer documento do editor. Embrulha o reducer do módulo
// e adiciona pilhas past/future + coalescing.
//
// Duas regras do contrato do shell moram aqui:
//  1. O reducer devolve a MESMA referência num no-op → o history não empilha nada
//     (é por isso que o reducer do módulo precisa honrar a identidade).
//  2. Coalescing por chave: ações consecutivas com a mesma `coalesceKey` (não nula)
//     colapsam numa entrada só de undo — digitar "PATO" é UM undo, não quatro.
//
// Dirty = `revision !== savedRevision` (dois inteiros, sem deep compare).

export interface HistoryState<TState> {
  present: TState;
  past: TState[];
  future: TState[];
  /** chave de coalescing da última mudança comitada (null = não coalesce a próxima) */
  lastKey: string | null;
  revision: number;
  savedRevision: number;
}

export type HistoryAction<TAction> =
  | { kind: 'do'; action: TAction }
  | { kind: 'undo' }
  | { kind: 'redo' }
  | { kind: 'markSaved' };

export function initHistory<TState>(present: TState): HistoryState<TState> {
  return { present, past: [], future: [], lastKey: null, revision: 0, savedRevision: 0 };
}

export function isDirty(state: HistoryState<unknown>): boolean {
  return state.revision !== state.savedRevision;
}

export function canUndo(state: HistoryState<unknown>): boolean {
  return state.past.length > 0;
}

export function canRedo(state: HistoryState<unknown>): boolean {
  return state.future.length > 0;
}

export function withHistory<TState, TAction>(
  reduce: (state: TState, action: TAction) => TState,
  coalesceKey: (action: TAction) => string | null,
) {
  return function historyReducer(
    state: HistoryState<TState>,
    ha: HistoryAction<TAction>,
  ): HistoryState<TState> {
    switch (ha.kind) {
      case 'do': {
        const nextPresent = reduce(state.present, ha.action);
        if (nextPresent === state.present) return state; // no-op → não empilha nada

        const key = coalesceKey(ha.action);
        const coalesce = key != null && key === state.lastKey;
        return {
          present: nextPresent,
          // Ao coalescer, NÃO empilha o present intermediário: a entrada aberta cobre
          // toda a sequência (o past guarda o estado anterior ao início dela).
          past: coalesce ? state.past : [...state.past, state.present],
          future: [],
          lastKey: key,
          revision: state.revision + 1,
          savedRevision: state.savedRevision,
        };
      }

      case 'undo': {
        if (state.past.length === 0) return state;
        const previous = state.past[state.past.length - 1];
        return {
          present: previous,
          past: state.past.slice(0, -1),
          future: [state.present, ...state.future],
          lastKey: null, // depois de um undo, a próxima ação abre entrada nova
          revision: state.revision + 1,
          savedRevision: state.savedRevision,
        };
      }

      case 'redo': {
        if (state.future.length === 0) return state;
        const next = state.future[0];
        return {
          present: next,
          past: [...state.past, state.present],
          future: state.future.slice(1),
          lastKey: null,
          revision: state.revision + 1,
          savedRevision: state.savedRevision,
        };
      }

      case 'markSaved':
        return { ...state, savedRevision: state.revision };
    }
  };
}
